// Live updates: when a row in one of these tables changes (anyone, any device), call back.
// Supabase Realtime + row-level security, so people only hear about rows they can see.
import { useEffect, useRef } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from './supabase';

type Change = RealtimePostgresChangesPayload<Record<string, unknown>>;
let seq = 0;

export function useLive(tables: string[], onChange: (c: Change) => void, opts: { filter?: string; enabled?: boolean } = {}) {
  const cb = useRef(onChange);
  cb.current = onChange;
  const key = tables.join(',') + '|' + (opts.filter ?? '');
  const enabled = opts.enabled ?? true;

  useEffect(() => {
    if (!enabled || tables.length === 0) return;
    // a unique name per hook instance (two instances with the same name would share one channel)
    const ch = supabase.channel(`pmc3-live-${key}-${++seq}`);
    for (const t of tables) {
      ch.on('postgres_changes', { event: '*', schema: 'public', table: t, ...(opts.filter ? { filter: opts.filter } : {}) }, (p) =>
        cb.current(p as Change),
      );
    }
    ch.subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
}

/** Wrap a reload so a burst of changes (e.g. undo of a batch) only reloads once. */
export function debounce(fn: () => void, ms = 350) {
  let t: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (t) clearTimeout(t);
    t = setTimeout(fn, ms);
  };
}
